import { useEffect, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";
import Button1 from "../../../components/form/Button1";
import updateFlightSetting from "../../../controllers/settings/supplier/updateFlightSetting";

const SUPPLIER_SETTING_FIELDS = [
  { key: "autoTicketingEnabledSuppliers", label: "Auto Ticketing Enabled Suppliers" },
  { key: "changeEnabledSuppliers", label: "Change Enabled Suppliers" },
  { key: "refundEnabledSuppliers", label: "Refund Enabled Suppliers" },
  { key: "voidEnabledSuppliers", label: "Void Enabled Suppliers" },
  { key: "autoChangeEnabledSuppliers", label: "Auto Change Enabled Suppliers" },
  { key: "autoRefundEnabledSuppliers", label: "Auto Refund Enabled Suppliers" },
  { key: "autoVoidEnabledSuppliers", label: "Auto Void Enabled Suppliers" },
  { key: "autoBaggageEnabledSuppliers", label: "Auto Baggage Enabled Suppliers" },
  { key: "autoSeatEnabledSuppliers", label: "Auto Seat Enabled Suppliers" },
  { key: "prioritySuppliers", label: "Priority Suppliers" },
];

function normalizeList(list) {
  return Array.isArray(list) ? [...list] : [];
}

export default function BulkSupplierSettings({
  data,
  close,
  callback,
  onSelectField,
  draftForm,
  onDraftChange,
}) {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (draftForm) {
      setForm(draftForm);
      return;
    }
    const next = {};
    SUPPLIER_SETTING_FIELDS.forEach(({ key }) => {
      next[key] = normalizeList(data?.[key]);
    });
    setForm(next);
  }, [data, draftForm]);

  const supplierNameMap = useMemo(() => {
    const map = {};
    (data?.list || []).forEach((supplier) => {
      if (supplier?.value) {
        map[supplier.value] =
          supplier?.name || supplier?.alias || supplier.value;
      }
    });
    return map;
  }, [data?.list]);

  function openPicker(field) {
    const currentForm = { ...form };
    onDraftChange && onDraftChange(currentForm);
    onSelectField &&
      onSelectField({
        key: field.key,
        label: field.label,
        selected: normalizeList(currentForm[field.key]),
        form: currentForm,
      });
  }

  async function handleSubmit() {
    setLoading(true);
    const payload = {};
    SUPPLIER_SETTING_FIELDS.forEach(({ key }) => {
      payload[key] = normalizeList(form[key]);
    });

    const res = await updateFlightSetting(payload);
    setLoading(false);

    if (res.return) {
      enqueueSnackbar("Successful", { variant: "success" });
      callback && callback();
      close && close();
    } else {
      enqueueSnackbar(res.msg, { variant: "error" });
    }
  }

  return (
    <div className="flex flex-col gap-4 max-h-[70vh] overflow-auto p-1">
      <span className="block text-sm text-gray-600">
        Choose suppliers for each setting. Open a setting to toggle suppliers by
        code and alias.
      </span>
      {SUPPLIER_SETTING_FIELDS.map((field) => {
        const selected = normalizeList(form[field.key]);
        return (
          <div
            key={field.key}
            className="flex flex-col gap-3 border rounded-md p-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="block text-sm font-medium text-gray-800">
                  {field.label}
                </span>
                <span className="block text-xs text-gray-500 mt-1">
                  {selected.length} selected
                </span>
              </div>
              <Button1
                className="!w-auto !px-3 !py-1.5 !text-sm"
                onClick={() => openPicker(field)}
              >
                Select
              </Button1>
            </div>
            {selected.length ? (
              <div className="flex flex-wrap gap-2">
                {selected.map((code) => (
                  <span
                    key={code}
                    className="inline-flex items-center gap-1 rounded-full bg-[#F0F6FC] px-2.5 py-1 text-xs text-gray-700"
                  >
                    <span className="font-semibold">
                      {supplierNameMap[code] || code}
                    </span>
                    {supplierNameMap[code] ? (
                      <span className="text-gray-500">· {code}</span>
                    ) : null}
                  </span>
                ))}
              </div>
            ) : (
              <span className="block text-xs text-gray-400">
                No suppliers selected
              </span>
            )}
          </div>
        );
      })}
      <div className="flex gap-4 items-center sticky bottom-0 bg-white pt-2">
        <div>
          <Button1 variant="outlined" onClick={() => close && close()}>
            Cancel
          </Button1>
        </div>
        <Button1 loading={loading} onClick={handleSubmit}>
          Save
        </Button1>
      </div>
    </div>
  );
}
